import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import api from "../../services/api";
import { Link } from "react-router-dom";
import { IoArrowForwardOutline } from "react-icons/io5";

import { classNames, getDaysInMonth } from "../../utils";

const getBudgetTypeColor = (budgetType) => {
  switch (budgetType) {
    case "project":
      return "bg-green-500";
    case "prediction":
      return "bg-purple-500";
    case "admin":
      return "bg-yellow-500";
    case "invest":
      return "bg-orange-500";
    case "overbudget":
      return "bg-red-500";
    default:
      return "bg-gray-500";
  }
};

const getBudgetTypeSvgColor = (budgetType) => {
  switch (budgetType) {
    case "project":
      return "#10B981";
    case "prediction":
      return "#8B5CF6";
    case "admin":
      return "#F59E0B";
    case "invest":
      return "#F97316";
    case "overbudget":
      return "#EF4444";
    default:
      return "#6B7280";
  }
};

const Activities = () => {
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.Auth.user);
  const [value, setValue] = useState();
  const [total, setTotal] = useState();
  const [activities, setActivities] = useState([]);

  const d = new Date();
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));

  async function get() {
    try {
      setLoading(true);
      const activitiesData = [];

      const previousMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1));
      const response1 = await api.post(`/activity/search`, { date: previousMonth.getTime(), userId: user._id });
      activitiesData.push(...response1?.data);

      const response2 = await api.post(`/activity/search`, { date: date.getTime(), userId: user._id });
      activitiesData.push(...response2?.data);

      setActivities(activitiesData);

      const hoursPerDay = compute(activitiesData);

      for (let i = 0; i < hoursPerDay.length; i++) {
        setValue((p) => p + hoursPerDay[i].declaredHours);
        setTotal((p) => p + 8);
      }

      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  }

  const compute = (activities) => {
    const differentsMonhtsInActivities = activities.reduce((acc, a) => {
      if (!acc.includes(a.date)) {
        acc.push(a.date);
      }
      return acc;
    }, []);

    // get all the possible days in the months of the activities
    const days = differentsMonhtsInActivities.reduce((acc, curr) => {
      const _date = new Date(curr);
      const _days = getDaysInMonth(_date.getUTCMonth(), _date.getUTCFullYear());
      return [...acc, ..._days];
    }, []);

    // get all the workings days, we do not care about the weekends
    const workingDays = getWorkingDays(days);

    // remove the days in the future
    const today = new Date();
    const workingDaysInThePast = workingDays.filter((d) => d.getTime() <= today.getTime());

    // get the last 20 days
    const last20Days = workingDaysInThePast.slice(-20);

    // get the declared hours for each day
    const hoursPerDay = [];
    for (let _d of last20Days) {
      const d = new Date(_d);
      const month = d.getMonth();
      const day = d.getDate();
      const year = d.getFullYear();

      // get the activities for the month
      const activitiesForMonth = activities.filter((a) => {
        const date = new Date(a.date);
        return date.getMonth() === month && date.getFullYear() === year;
      });

      // get the declared hours for the day for every activity
      const declaredHours = activitiesForMonth.reduce((acc, a) => {
        const detail = a.detail[day - 1];
        return acc + detail.value;
      }, 0);

      hoursPerDay.push({ date: d, declaredHours });
    }

    return hoursPerDay;
  };

  const getWorkingDays = (days) => {
    return days.reduce((acc, a) => {
      const day = a.getDay();
      if (day === 0 || day == 6) return acc;
      return [...acc, a];
    }, []);
  };

  const calculateBudgetTypePercentages = () => {
    const totalHours = activities.reduce((acc, a) => acc + a.total, 0);
    if (totalHours === 0) return {};

    const budgetTypeHours = activities.reduce((acc, activity) => {
      if (!acc[activity.budgetType]) {
        acc[activity.budgetType] = 0;
      }
      acc[activity.budgetType] += activity.total;
      return acc;
    }, {});

    const order = ["project", "prediction", "admin", "invest", "overbudget"];
    const sortedBudgetTypePercentages = Object.fromEntries(Object.entries(budgetTypeHours).sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0])));

    // Convert to percentages
    Object.keys(sortedBudgetTypePercentages).forEach((key) => {
      sortedBudgetTypePercentages[key] = (sortedBudgetTypePercentages[key] / totalHours) * 100;
    });

    return sortedBudgetTypePercentages;
  };

  useEffect(() => {
    setValue(0);
    setTotal(0);
    get();
  }, []);

  if (loading) return <Skeleton />;

  let color = "";
  if (value / total >= 0.8) color = "#34D399";
  if (value / total < 0.8) color = "#FBBF24";
  if (value / total < 0.5) color = "#F87171";

  const budgetTypePercentages = calculateBudgetTypePercentages();

  return (
    <div className={classNames(" border-[1px] border-gray-200 rounded-md overflow-hidden h-full bg-white")}>
      <Link to="/activity" className="group relative flex flex-col items-center h-full">
        <div className="w-full p-3 flex items-center justify-between">
          <div className="w-full flex justify-between items-center">
            <h1 className="flex items-center text-base font-semibold text-gray-900 gap-2">
              <span>My activities</span>
            </h1>
            <span className="text-xs text-gray-500">last 20 days</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {["project", "prediction", "admin", "invest", "overbudget"].map((budgetType) => (
            <div key={budgetType} className="flex items-center gap-1">
              <div className={`w-3 h-3 rounded-full ${getBudgetTypeColor(budgetType)}`} />
              <span className="text-[10px] text-gray-600">{budgetType}</span>
            </div>
          ))}
        </div>
        <div to="/activity" className="flex-1 group relative flex flex-col items-center justify-between">
          <CircleGraph value={value} total={total} color={color} text={`${Math.round((value / total) * 100)}%`} budgetTypePercentages={budgetTypePercentages} />
          <div className={`text-sm flex w-full items-center justify-center text-gray-500 mb-3 transition ease-in-out group-hover:translate-y-32`}>
            {value} / {total} hours
          </div>
          <span className={`absolute -bottom-32 flex w-full items-center justify-center text-black font-bold transition ease-in-out group-hover:-translate-y-36`}>
            Fill in <IoArrowForwardOutline />
          </span>
        </div>
      </Link>
    </div>
  );
};

const CircleGraph = ({ value, total = 100, text, color, budgetTypePercentages }) => {
  const radius = 30;
  const percentage = value / total;

  return (
    <div className="relative flex items-center justify-center w-28 h-28">
      {/* Gray background circle */}
      <svg className="-rotate-90" width="100%" height="100%" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={radius} fill="transparent" stroke={"#DFE1E5"} strokeWidth="4" />
      </svg>

      {/* Budget type pie chart with missing portion */}
      {budgetTypePercentages && Object.keys(budgetTypePercentages).length > 0 && (
        <svg className="absolute -rotate-90" width="100%" height="100%" viewBox="0 0 80 80">
          {(() => {
            let currentAngle = 0;

            // First, draw the budget type segments
            const segments = Object.entries(budgetTypePercentages).map(([budgetType, percentage], index) => {
              // Scale the budget type percentage by the overall completion percentage
              const scaledPercentage = (percentage / 100) * (value / total) * 100;
              const angle = (scaledPercentage / 100) * 2 * Math.PI;
              const x1 = 40 + radius * Math.cos(currentAngle);
              const y1 = 40 + radius * Math.sin(currentAngle);
              const x2 = 40 + radius * Math.cos(currentAngle + angle);
              const y2 = 40 + radius * Math.sin(currentAngle + angle);

              const largeArcFlag = angle > Math.PI ? 1 : 0;
              const pathData = [`M 40 40`, `L ${x1} ${y1}`, `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`, `Z`].join(" ");

              currentAngle += angle;

              return <path key={budgetType} d={pathData} fill={getBudgetTypeSvgColor(budgetType)} stroke="white" strokeWidth="0.5" />;
            });

            // Then, draw the missing portion if there is one
            const missingPercentage = 100 - (value / total) * 100;
            if (missingPercentage > 0) {
              const missingAngle = (missingPercentage / 100) * 2 * Math.PI;
              const x1 = 40 + radius * Math.cos(currentAngle);
              const y1 = 40 + radius * Math.sin(currentAngle);
              const x2 = 40 + radius * Math.cos(currentAngle + missingAngle);
              const y2 = 40 + radius * Math.sin(currentAngle + missingAngle);

              const largeArcFlag = missingAngle > Math.PI ? 1 : 0;
              const missingPathData = [`M 40 40`, `L ${x1} ${y1}`, `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`, `Z`].join(" ");

              segments.push(<path key="missing" d={missingPathData} fill="#DFE1E5" stroke="white" strokeWidth="0.5" />);
            }

            return segments;
          })()}
        </svg>
      )}

      <div className="absolute flex flex-col items-center justify-center bg-white rounded-full p-2 h-1/2 w-1/2">
        <div className="font-semibold">{text}</div>
      </div>
    </div>
  );
};

const Skeleton = () => (
  <div className="bg-white flex flex-col justify-between no-underline	hover:no-underline rounded-md p-3 border border-gray-200 h-full overflow-hidden">
    <div className="bg-gray-200 w-24 h-4 rounded-md mb-4" />
    <div className="flex flex-row items-center justify-center w-full h-full">
      <div className="bg-gray-200 rounded-full w-20 h-20 mr-2" />
    </div>
  </div>
);

export default Activities;
